// services/knowledgeBases.service.js
import knowledgeBasesClient, {
  handleKnowledgeBaseError,
} from '../api/knowledgeBasesClient';

/**
 * Knowledge Bases Service — wrappers REST sobre el `rag_service` Lambda.
 *
 * Convención (alineada con database/account/billing services):
 *   Cada método devuelve `{ success, status, data | error }`.
 *   - `success: true` → `data` es el body parseado.
 *   - `success: false` → `error` es `{status, code, message, data}` producido
 *     por `handleKnowledgeBaseError`.
 *
 * Wire format: JSON camelCase (accountId, projectId, mimeType, contentBase64,
 * topK, …). El backend valida el acceso del caller a la `accountId` del recurso.
 *
 * Costos (coin economy ON): KB CRUD = 0; subir doc = 5/15/50 (S/M/L);
 * search = 2. Un saldo insuficiente devuelve 402 INSUFFICIENT_COINS.
 */

const buildQuery = (params) => {
  const search = new URLSearchParams();
  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.append(key, String(value));
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
};

// El rag_service devuelve JSON directo; mantenemos un unwrap defensivo por si
// la respuesta viniera envuelta (body/data) como en otros services.
const unwrap = (responseData) =>
  responseData?.body?.data ?? responseData?.body ?? responseData?.data ?? responseData;

const KnowledgeBasesService = {
  // ---------------------------------------------------------------
  // Knowledge Bases (CRUD)
  // ---------------------------------------------------------------

  /**
   * GET /kbs?accountId=&projectId=&limit=&offset=
   * Lista las KBs de una cuenta. Response: `{ items, total, limit, offset }`.
   */
  async listKnowledgeBases(accountId, { projectId, limit, offset } = {}) {
    try {
      const qs = buildQuery({ accountId, projectId, limit, offset });
      const response = await knowledgeBasesClient.get(`/kbs${qs}`);
      return { success: true, status: response.status, data: unwrap(response.data) };
    } catch (error) {
      return { success: false, error: handleKnowledgeBaseError(error) };
    }
  },

  /**
   * GET /kbs/{kbId}
   * Trae una KB con su estado (provisioning|ready|failed|deleting).
   */
  async getKnowledgeBase(kbId) {
    try {
      const response = await knowledgeBasesClient.get(`/kbs/${kbId}`);
      return { success: true, status: response.status, data: unwrap(response.data) };
    } catch (error) {
      return { success: false, error: handleKnowledgeBaseError(error) };
    }
  },

  /**
   * POST /kbs
   * Crea una KB. Body: { name, accountId, projectId?, description? }.
   * Provisión síncrona → la respuesta vuelve con `status: "ready"`. (0 coins)
   */
  async createKnowledgeBase({ name, accountId, projectId, description }) {
    try {
      const body = { name, accountId };
      if (projectId) body.projectId = projectId;
      if (description) body.description = description;
      const response = await knowledgeBasesClient.post('/kbs', body);
      return { success: true, status: response.status, data: unwrap(response.data) };
    } catch (error) {
      return { success: false, error: handleKnowledgeBaseError(error) };
    }
  },

  /**
   * PATCH /kbs/{kbId}
   * Edita metadata. Solo `name` y `description` son mutables. (0 coins)
   */
  async updateKnowledgeBase(kbId, { name, description }) {
    try {
      const body = {};
      if (name !== undefined) body.name = name;
      if (description !== undefined) body.description = description;
      const response = await knowledgeBasesClient.patch(`/kbs/${kbId}`, body);
      return { success: true, status: response.status, data: unwrap(response.data) };
    } catch (error) {
      return { success: false, error: handleKnowledgeBaseError(error) };
    }
  },

  /**
   * DELETE /kbs/{kbId}
   * Borra la KB en cascada (Bedrock KB + data source + prefijo S3 + fila DB +
   * documentos por FK). Responde 204. (0 coins)
   */
  async deleteKnowledgeBase(kbId) {
    try {
      const response = await knowledgeBasesClient.delete(`/kbs/${kbId}`);
      return { success: true, status: response.status, data: unwrap(response.data) };
    } catch (error) {
      return { success: false, error: handleKnowledgeBaseError(error) };
    }
  },

  // ---------------------------------------------------------------
  // Documents
  // ---------------------------------------------------------------

  /**
   * GET /kbs/{kbId}/documents?limit=&offset=
   * Lista documentos. Response: `{ items, total, limit, offset }`.
   */
  async listDocuments(kbId, { limit, offset } = {}) {
    try {
      const qs = buildQuery({ limit, offset });
      const response = await knowledgeBasesClient.get(`/kbs/${kbId}/documents${qs}`);
      return { success: true, status: response.status, data: unwrap(response.data) };
    } catch (error) {
      return { success: false, error: handleKnowledgeBaseError(error) };
    }
  },

  /**
   * GET /kbs/{kbId}/documents/{docId}
   */
  async getDocument(kbId, docId) {
    try {
      const response = await knowledgeBasesClient.get(`/kbs/${kbId}/documents/${docId}`);
      return { success: true, status: response.status, data: unwrap(response.data) };
    } catch (error) {
      return { success: false, error: handleKnowledgeBaseError(error) };
    }
  },

  /**
   * POST /kbs/{kbId}/documents
   * Sube un documento (contenido base64). Body:
   *   { filename, mimeType, contentBase64, pageCount? }
   * Responde 202 con `status: "indexing"`. (Cobra 5/15/50 coins según tier S/M/L)
   *
   * ⚠️ Límite efectivo ~4.5 MB (payload síncrono Lambda). Validar tamaño antes.
   */
  async uploadDocument(kbId, { filename, mimeType, contentBase64, pageCount }) {
    try {
      const body = { filename, mimeType, contentBase64 };
      if (pageCount !== undefined && pageCount !== null) body.pageCount = pageCount;
      const response = await knowledgeBasesClient.post(`/kbs/${kbId}/documents`, body);
      return { success: true, status: response.status, data: unwrap(response.data) };
    } catch (error) {
      return { success: false, error: handleKnowledgeBaseError(error) };
    }
  },

  /**
   * DELETE /kbs/{kbId}/documents/{docId}
   * Borra el doc (Bedrock + objeto S3 + fila DB). Responde 204.
   */
  async deleteDocument(kbId, docId) {
    try {
      const response = await knowledgeBasesClient.delete(`/kbs/${kbId}/documents/${docId}`);
      return { success: true, status: response.status, data: unwrap(response.data) };
    } catch (error) {
      return { success: false, error: handleKnowledgeBaseError(error) };
    }
  },

  // ---------------------------------------------------------------
  // Search (retrieval) — panel de prueba en la UI. Cobra 2 coins por consulta.
  // ---------------------------------------------------------------

  /**
   * POST /kbs/{kbId}/search
   * Body: { query, topK? }. Response: `{ chunks:[{content, score, source}], total }`.
   */
  async search(kbId, { query, topK } = {}) {
    try {
      const body = { query };
      if (topK !== undefined && topK !== null) body.topK = topK;
      const response = await knowledgeBasesClient.post(`/kbs/${kbId}/search`, body);
      return { success: true, status: response.status, data: unwrap(response.data) };
    } catch (error) {
      return { success: false, error: handleKnowledgeBaseError(error) };
    }
  },
};

export default KnowledgeBasesService;
